import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { PageHeader } from "@/components/ui/page-header";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { TailoredDocumentService, TailoredDocumentSession } from "@/services/tailoredDocumentService";
import { supabase } from "@/integrations/supabase/client";
import { BulkResumeService } from "@/services/bulkResumeService";
import { ResumeMatchBreakdown } from '@/components/ResumeMatchBreakdown';
import { Progress } from "@/components/ui/progress";
import jsPDF from 'jspdf';
import { 
  FileText, 
  Mail, 
  Heart, 
  User, 
  Linkedin, 
  Send,
  Calendar,
  Building2,
  Briefcase,
  Loader2,
  Eye,
  Download,
  BarChart3,
  Monitor,
  FileJson,
  ArrowLeft,
  TrendingUp,
  CheckCircle,
  Check,
  Target,
  Trash2
} from 'lucide-react';

const getDocumentIcon = (type: string) => {
  switch (type) {
    case 'resume': return <FileText className="w-4 h-4" />;
    case 'cover_letter': return <Mail className="w-4 h-4" />;
    case 'thank_you_letter': return <Heart className="w-4 h-4" />;
    case 'executive_bio': return <User className="w-4 h-4" />;
    case 'linkedin_profile': return <Linkedin className="w-4 h-4" />;
    case 'outreach_letter': return <Send className="w-4 h-4" />;
    default: return <FileText className="w-4 h-4" />;
  }
};

const getDocumentTypeLabel = (type: string) => {
  switch (type) {
    case 'resume': return 'Resume';
    case 'cover_letter': return 'Cover Letter';
    case 'thank_you_letter': return 'Thank You Letter';
    case 'executive_bio': return 'Executive Bio';
    case 'linkedin_profile': return 'LinkedIn Profile';
    case 'outreach_letter': return 'Outreach Letter';
    default: return type;
  }
};

export default function TailoredDocumentDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [session, setSession] = useState<TailoredDocumentSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAnalysisBreakdown, setShowAnalysisBreakdown] = useState(false);
  const [activeDocumentTab, setActiveDocumentTab] = useState<string>('resume');
  const [documentJsonMap, setDocumentJsonMap] = useState<Map<string, any>>(new Map());
  const [isGeneratingJson, setIsGeneratingJson] = useState<string | null>(null);
  const [activeJsonTab, setActiveJsonTab] = useState<string>('');
  const [activeViewTab, setActiveViewTab] = useState('text');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingWordDoc, setIsGeneratingWordDoc] = useState(false);
  const [isOpeningWordViewer, setIsOpeningWordViewer] = useState<string | null>(null);
  const [isDeletingJson, setIsDeletingJson] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());

  // Get session data from navigation state or load by ID from URL
  const sessionData = location.state?.session;
  const sessionId = location.pathname.split('/').pop();


  useEffect(() => {
    if (sessionData) {
      setSession(sessionData);
      setIsLoading(false);
      
      // Load JSON data directly from resumejs table for this user - NO document ID dependency
      console.log('🔍 DEBUG: Loading ALL user JSON data from resumejs table...');
      const loadAllUserJsonData = async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          const jsonMap = new Map();

          // First, check if documents already have JSON data in their document_json_data field
          console.log('📊 DEBUG: Checking for embedded JSON data in documents...');
          for (const doc of sessionData.documents) {
            if (doc.document_json_data) {
              console.log(`✅ DEBUG: Found embedded JSON for document ${doc.id} (${doc.document_type})`);
              const jsonString = typeof doc.document_json_data === 'string' 
                ? doc.document_json_data 
                : JSON.stringify(doc.document_json_data, null, 2);
              jsonMap.set(doc.id, jsonString);
            }
          }

          // Then, get additional JSON data from resumejs table for this user
          const { data: allJsonData, error } = await supabase
            .from('resumejs')
            .select('*')
            .eq('user_id', user.id);

          if (error) {
            console.error('Error loading user JSON data:', error);
            return;
          }

          console.log('📊 DEBUG: Found JSON data entries in resumejs:', allJsonData?.length || 0);
          
          // Process all JSON entries from resumejs table
          if (allJsonData) {
            for (const jsonEntry of allJsonData) {
              console.log(`📄 DEBUG: Processing JSON entry:`, {
                document_id: jsonEntry.document_id,
                document_type: jsonEntry.document_type,
                title: jsonEntry.title,
                hasJsonData: !!jsonEntry.json_data,
                dataSize: jsonEntry.json_data ? JSON.stringify(jsonEntry.json_data).length : 0
              });

              // Try to match with session documents if they exist, but display JSON regardless
              const matchingDoc = sessionData.documents.find(doc => doc.id === jsonEntry.document_id);
              
              if (matchingDoc && !jsonMap.has(matchingDoc.id)) {
                // Found matching document and no embedded JSON - use resumejs data
                const jsonString = typeof jsonEntry.json_data === 'string' ? jsonEntry.json_data : JSON.stringify(jsonEntry.json_data, null, 2);
                jsonMap.set(matchingDoc.id, jsonString);
                console.log(`✅ DEBUG: Matched JSON to document ${matchingDoc.id} (${matchingDoc.document_type})`);
              } else if (matchingDoc) {
                console.log(`⏭️ DEBUG: Document ${matchingDoc.id} already has embedded JSON, skipping resumejs entry`);
              } else {
                // No matching document - use a generated key based on document type and timestamp
                const fallbackKey = `${jsonEntry.document_type}_${jsonEntry.document_id || 'standalone'}`;
                const jsonString = typeof jsonEntry.json_data === 'string' ? jsonEntry.json_data : JSON.stringify(jsonEntry.json_data, null, 2);
                jsonMap.set(fallbackKey, jsonString);
                console.log(`📌 DEBUG: Added standalone JSON entry with key: ${fallbackKey}`);
              }
            }
          }

          console.log(`📊 DEBUG: Final JSON map size: ${jsonMap.size} JSON entries loaded`);
          setDocumentJsonMap(jsonMap);
        } catch (error) {
          console.error('Error loading all user JSON data:', error);
        }
      };
      
      loadAllUserJsonData();
      
      if (sessionData.documents.length > 0) {
        // Always prioritize resume as the first selected tab
        const resumeDoc = sessionData.documents.find(doc => doc.document_type === 'resume');
        setActiveDocumentTab(resumeDoc ? 'resume' : sessionData.documents[0].document_type);
      }
    } else if (sessionId && user) {
      // Load session by ID if not passed through state
      loadSessionById(sessionId);
    } else {
      setIsLoading(false);
    }
  }, [sessionData, sessionId, user]);

  const loadSessionById = async (id: string) => {
    try {
      setIsLoading(true);
      const sessions = await TailoredDocumentService.getTailoredDocumentSessions();
      const foundSession = sessions.find(s => s.session_id === id);
      if (foundSession) {
        setSession(foundSession);
        
        // Load ALL user JSON data from resumejs table - NO document ID dependency
        console.log('🔍 DEBUG: Loading ALL user JSON data from resumejs table...');
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          const jsonMap = new Map();

          // First, check if documents already have JSON data in their document_json_data field
          console.log('📊 DEBUG: Checking for embedded JSON data in documents...');
          for (const doc of foundSession.documents) {
            if (doc.document_json_data) {
              console.log(`✅ DEBUG: Found embedded JSON for document ${doc.id} (${doc.document_type})`);
              const jsonString = typeof doc.document_json_data === 'string' 
                ? doc.document_json_data 
                : JSON.stringify(doc.document_json_data, null, 2);
              jsonMap.set(doc.id, jsonString);
            }
          }

          // Then, get additional JSON data from resumejs table for this user
          const { data: allJsonData, error } = await supabase
            .from('resumejs')
            .select('*')
            .eq('user_id', user.id);

          if (error) {
            console.error('Error loading user JSON data:', error);
            return;
          }

          console.log('📊 DEBUG: Found JSON data entries in resumejs:', allJsonData?.length || 0);
          
          // Process all JSON entries from resumejs table
          if (allJsonData) {
            for (const jsonEntry of allJsonData) {
              console.log(`📄 DEBUG: Processing JSON entry:`, {
                document_id: jsonEntry.document_id,
                document_type: jsonEntry.document_type,
                title: jsonEntry.title,
                hasJsonData: !!jsonEntry.json_data
              });

              // Try to match with session documents if they exist, but display JSON regardless
              const matchingDoc = foundSession.documents.find(doc => doc.id === jsonEntry.document_id);
              
              if (matchingDoc && !jsonMap.has(matchingDoc.id)) {
                // Found matching document and no embedded JSON - use resumejs data
                const jsonString = typeof jsonEntry.json_data === 'string' ? jsonEntry.json_data : JSON.stringify(jsonEntry.json_data, null, 2);
                jsonMap.set(matchingDoc.id, jsonString);
                console.log(`✅ DEBUG: Matched JSON to document ${matchingDoc.id} (${matchingDoc.document_type})`);
              } else if (matchingDoc) {
                console.log(`⏭️ DEBUG: Document ${matchingDoc.id} already has embedded JSON, skipping resumejs entry`);
              } else {
                // No matching document - use a generated key based on document type and timestamp
                const fallbackKey = `${jsonEntry.document_type}_${jsonEntry.document_id || 'standalone'}`;
                const jsonString = typeof jsonEntry.json_data === 'string' ? jsonEntry.json_data : JSON.stringify(jsonEntry.json_data, null, 2);
                jsonMap.set(fallbackKey, jsonString);
                console.log(`📌 DEBUG: Added standalone JSON entry with key: ${fallbackKey}`);
              }
            }
          }

          console.log(`📊 DEBUG: Final JSON map size: ${jsonMap.size} JSON entries loaded`);
          setDocumentJsonMap(jsonMap);
        } catch (error) {
          console.error('Error loading all user JSON data:', error);
        }
        
        // JSON data loaded - no auto-generation to prevent multiple documents opening
        
        if (foundSession.documents.length > 0) {
          // Always prioritize resume as the first selected tab
          const resumeDoc = foundSession.documents.find(doc => doc.document_type === 'resume');
          setActiveDocumentTab(resumeDoc ? 'resume' : foundSession.documents[0].document_type);
        }
      } else {
        toast({
          title: "Session Not Found",
          description: "The requested tailored document session could not be found.",
          variant: "destructive",
        });
        navigate(-1);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast({
        title: "Error",
        description: "Failed to load tailored document session.",
        variant: "destructive",
      });
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getImprovement = (session: TailoredDocumentSession): number => {
    const resume = session.resume_document;
    if (!resume || !resume.match_score || !resume.original_match_score) return 0;
    return resume.match_score - resume.original_match_score;
  };

  const handleGenerateJsonData = async (document: any, documentType: string) => {
    const user = await supabase.auth.getUser();
    if (!user.data.user) return;

    if (!document || !session) {
      toast({
        title: "Document Not Found",
        description: `Cannot generate JSON for ${documentType}.`,
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingJson(documentType);

    try {
      console.log(`🔄 DEBUG: Starting JSON generation for document ${document.id} (${documentType})`);
      
      // Call edge function to parse content into structured JSON
      const { data, error } = await supabase.functions.invoke('parse-resume-to-json', {
        body: {
          resumeContent: document.content,
          documentType: documentType
        }
      });

      if (error) {
        console.error('❌ DEBUG: Edge function error:', error);
        throw error;
      }

      if (!data?.success || !data?.parsedData) {
        console.error('❌ DEBUG: Invalid response from edge function:', data);
        throw new Error('Failed to parse document content into JSON');
      }

      console.log('✅ DEBUG: JSON parsing successful, data size:', JSON.stringify(data.parsedData).length);

      // Save ONLY to resumejs table
      await saveToResumeJsonTable(document.id, data.parsedData, documentType, document.title);

      // Update local state - store as string to preserve formatting
      const jsonString = typeof data.parsedData === 'string' ? data.parsedData : JSON.stringify(data.parsedData, null, 2);
      setDocumentJsonMap(prev => new Map(prev.set(document.id, jsonString)));

      toast({
        title: "JSON Generated!",
        description: `Successfully parsed ${documentType} content into structured JSON data.`,
      });

      console.log('✅ DEBUG: JSON generation complete for document', document.id);

    } catch (error: any) {
      console.error('❌ DEBUG: JSON generation failed:', error);
      toast({
        title: "JSON Generation Failed", 
        description: error.message || `Failed to generate JSON for ${documentType}`,
        variant: "destructive",
      });
    } finally {
      setIsGeneratingJson(null);
    }
  };

  const handleGenerateAllJsonData = async () => {
    const user = await supabase.auth.getUser();
    if (!user.data.user) return;

    if (!session || session.documents.length === 0) {
      toast({
        title: "No Documents",
        description: "No documents available to generate JSON for.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingJson('all');

    try {
      console.log('🔄 DEBUG: Starting bulk JSON generation for all documents');
      
      // Generate JSON for all documents that don't have it
      const documentsToProcess = session.documents.filter(doc => !documentJsonMap.has(doc.id));
      
      if (documentsToProcess.length === 0) {
        toast({
          title: "All Documents Ready",
          description: "All documents already have JSON data generated.",
        });
        return;
      }

      console.log(`📊 DEBUG: Processing ${documentsToProcess.length} documents`);

      const results = await Promise.allSettled(
        documentsToProcess.map(async (doc: any) => {
          console.log(`🔄 DEBUG: Processing document ${doc.id} (${doc.document_type})`);
          
          const { data, error } = await supabase.functions.invoke('parse-resume-to-json', {
            body: {
              resumeContent: doc.content,
              documentType: doc.document_type
            }
          });

          if (error) throw error;
          if (!data?.success || !data?.parsedData) {
            throw new Error(`Failed to parse ${doc.document_type} content`);
          }

          // Save ONLY to resumejs table
          await saveToResumeJsonTable(doc.id, data.parsedData, doc.document_type, doc.title);

          return { docId: doc.id, jsonData: data.parsedData };
        })
      );

      // Update local state for successful results
      const newJsonMap = new Map(documentJsonMap);
      let successCount = 0;
      let errorCount = 0;

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          newJsonMap.set(result.value.docId, result.value.jsonData);
          successCount++;
        } else {
          console.error('❌ DEBUG: Document processing failed:', result.reason);
          errorCount++;
        }
      });

      setDocumentJsonMap(newJsonMap);

      if (successCount > 0) {
        toast({
          title: "JSON Generation Complete!",
          description: `Successfully generated JSON for ${successCount} document(s).${errorCount > 0 ? ` ${errorCount} failed.` : ''}`,
        });
      } else {
        throw new Error('Failed to generate JSON for any documents');
      }

      console.log(`✅ DEBUG: Bulk JSON generation complete - ${successCount} success, ${errorCount} errors`);

    } catch (error: any) {
      console.error('❌ DEBUG: Bulk JSON generation failed:', error);
      toast({
        title: "JSON Generation Failed",
        description: error.message || "Failed to generate JSON for documents",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingJson(null);
    }
  };

  const handleSaveDocumentAsResume = async (document: any, documentType: string) => {
    const user = await supabase.auth.getUser();
    if (!user.data.user) return;

    if (!document || !session) {
      toast({
        title: "Document Not Found",
        description: `Cannot save ${documentType} as resume.`,
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingJson(document.id);

    try {
      // Save directly to resumes table as resume content
      const { error } = await supabase
        .from('resumes')
        .insert({
          user_id: user.data.user.id,
          title: `${document.title} - Resume`,
          content: document.content,
          job_title: document.job_title,
          company_name: document.company_name,
          job_description: document.job_description,
          processing_status: 'completed'
        });

      if (error) throw error;

      toast({
        title: "Resume Saved",
        description: `${getDocumentTypeLabel(documentType)} has been saved as a resume.`,
      });

    } catch (error) {
      console.error('Error saving resume:', error);
      toast({
        title: "Save Failed",
        description: `Failed to save ${documentType} as resume. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsGeneratingJson(null);
    }
  };

  const generateWordDocumentFromJson = async (document: any, jsonData: any) => {
    // Prevent multiple calls for the same document
    if (isOpeningWordViewer === document.id) return;
    
    setIsOpeningWordViewer(document.id);
    try {
      toast({
        title: "Generating Word Document",
        description: "Creating Word document from JSON data...",
      });

      // Use generate-bulk-resumes for all document types with correct templates
      let templateName = "ResumeTemplate.docx"; // Default template
      
      if (['cover_letter', 'thank_you_letter', 'outreach_letter'].includes(document.document_type)) {
        templateName = "Cover Letter, thank you letter, outreach letter docmosis template (35).docx";
      } else if (document.document_type === 'linkedin_profile') {
        templateName = "LinkedIn Profile docmosis template.docx";
      }
      
      const response = await supabase.functions.invoke('generate-bulk-resumes', {
        body: {
          ...jsonData,
          templateName: templateName
        }
      });
      const docmosisData = response.data;
      const docmosisError = response.error;

      if (docmosisError) throw docmosisError;
      if (!docmosisData.success) throw new Error('Failed to generate Word document');

      // Convert base64 to blob
      const binaryString = atob(docmosisData.files[0].data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const docmosisBlob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });

      // Generate appropriate filename based on document type
      let fileName;
      if (document.document_type === 'cover_letter') {
        const companyName = jsonData.Company && jsonData.Company !== '[Company Name]' 
          ? jsonData.Company.replace(/[\[\]]/g, '').trim()
          : 'Cover Letter';
        fileName = `${jsonData.Name || 'Document'} - ${companyName} Cover Letter 2025-${Date.now()}.docx`;
      } else {
        fileName = `${jsonData.Name || 'Resume'}-${session?.job_title || 'Document'}-${Date.now()}.docx`;
      }
      
      const filePath = `${user?.id}/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('word-documents')
        .upload(filePath, docmosisBlob, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const publicUrl = supabase.storage.from('word-documents').getPublicUrl(filePath).data.publicUrl;

      // Update the document with the Word file info
      await supabase
        .from('tailored_documents')
        .update({
          word_document_url: publicUrl,
          word_document_path: filePath,
          updated_at: new Date().toISOString()
        })
        .eq('id', document.id);

      toast({
        title: "Word Document Generated!",
        description: "Opening in Word viewer...",
      });

      // Redirect to Word document viewer with JSON data
      navigate('/word-document-viewer', {
        state: { 
          resumeJson: jsonData,
          tailoringSessionId: session.session_id,
          tailoredDocumentId: document.id
        }
      });

    } catch (error: any) {
      console.error('Error generating Word document:', error);
      toast({
        title: "Word Generation Failed",
        description: error.message || "Failed to generate Word document",
        variant: "destructive"
      });
    } finally {
      setIsOpeningWordViewer(null);
    }
  };

  const handleDeleteAllJsonData = async () => {
    if (!session) return;

    try {
      console.log('🗑️ DEBUG: Starting bulk JSON deletion for all documents');
      
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('User not authenticated');

      // Delete from resumejs table ONLY  
      const deletePromises = session.documents.map(async (doc: any) => {
        console.log(`🗑️ DEBUG: Deleting JSON for document ${doc.id} (${doc.document_type})`);
        
        const { error } = await supabase
          .from('resumejs')
          .delete()
          .eq('user_id', user.data.user.id)
          .eq('document_id', doc.id);

        if (error) throw error;
        console.log(`✅ DEBUG: JSON deleted for document ${doc.id}`);
      });

      await Promise.all(deletePromises);

      // Clear local state
      setDocumentJsonMap(new Map());

      toast({
        title: "All JSON Data Deleted",
        description: "All JSON data has been cleared from all documents.",
      });

      console.log('✅ DEBUG: Bulk JSON deletion complete');

    } catch (error: any) {
      console.error('❌ DEBUG: Bulk JSON deletion failed:', error);
      toast({
        title: "Deletion Failed",
        description: error.message || "Failed to delete JSON data",
        variant: "destructive",
      });
    }
  };

  // Save JSON data to independent resumejs table
  const saveToResumeJsonTable = async (documentId: string, jsonData: any, documentType: string, title: string) => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      // Store as JSON string to preserve exact formatting and key order
      const jsonString = typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData, null, 2);

      const { error } = await supabase
        .from('resumejs')
        .upsert({
          user_id: user.user.id,
          document_id: documentId,
          document_type: documentType,
          json_data: jsonString,
          title: title
        }, {
          onConflict: 'user_id,document_id'
        });

      if (error) {
        console.error('Error saving to resumejs table:', error);
      } else {
        console.log('Successfully saved JSON data to independent resumejs table');
      }
    } catch (error) {
      console.error('Error in saveToResumeJsonTable:', error);
    }
  };

  // Load JSON data from independent resumejs table
  const loadFromResumeJsonTable = async (documentId: string) => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return null;

      const { data, error } = await supabase
        .from('resumejs')
        .select('json_data')
        .eq('user_id', user.user.id)
        .eq('document_id', documentId)
        .single();

      if (error) {
        console.log('No data found in resumejs table for document:', documentId);
        return null;
      }

      // Return as string to preserve exact formatting
      return data?.json_data || null;
    } catch (error) {
      console.error('Error in loadFromResumeJsonTable:', error);
      return null;
    }
  };

  const handleOpenWordViewer = async (documentIds?: string[]) => {
    const user = await supabase.auth.getUser();
    if (!user.data.user) return;

    if (!session) {
      toast({
        title: "No Session",
        description: "Cannot open Word Viewer without a valid session.",
        variant: "destructive",
      });
      return;
    }

    // Use selected documents or all documents if none specified
    const docsToOpen = documentIds || Array.from(selectedDocuments);
    
    if (docsToOpen.length === 0) {
      toast({
        title: "No Documents Selected",
        description: "Please select at least one document to open in Word Viewer.",
        variant: "destructive",
      });
      return;
    }

    const documentsToOpen = session.documents.filter(doc => docsToOpen.includes(doc.id));
    
    if (documentsToOpen.length === 0) {
      toast({
        title: "Documents Not Found",
        description: "Selected documents could not be found.",
        variant: "destructive",
      });
      return;
    }

    setIsOpeningWordViewer('multiple');

    try {
      // Load JSON data for all selected documents
      const documentsWithJson = [];
      
      for (const document of documentsToOpen) {
        const jsonData = await loadFromResumeJsonTable(document.id);
        
        if (!jsonData) {
          toast({
            title: "Missing JSON Data",
            description: `${document.title} needs to be regenerated and saved to protected storage first.`,
            variant: "destructive",
          });
          continue; // Skip this document but continue with others
        }
        
        documentsWithJson.push({
          ...document,
          jsonData
        });
      }
      
      if (documentsWithJson.length === 0) {
        toast({
          title: "No Valid Documents",
          description: "None of the selected documents have valid JSON data.",
          variant: "destructive",
        });
        return;
      }

      console.log(`Opening Word Viewer with ${documentsWithJson.length} documents`);

      // Navigate to Word document viewer with all documents
      navigate('/word-document-viewer', {
        state: { 
          multipleDocuments: documentsWithJson.map(doc => ({
            resumeJson: doc.jsonData,
            tailoringSessionId: session.session_id,
            tailoredDocumentId: doc.id,
            documentType: doc.document_type,
            title: doc.title
          }))
        }
      });

      toast({
        title: "Opening Word Viewer",
        description: `Opening ${documentsWithJson.length} document(s) in Word Viewer...`,
      });

    } catch (error: any) {
      console.error('Error opening Word Viewer:', error);
      toast({
        title: "Word Viewer Error",
        description: error.message || "Failed to open Word Viewer",
        variant: "destructive",
      });
    } finally {
      setIsOpeningWordViewer(null);
    }
  };

  const handleDocumentSelection = (documentId: string, checked: boolean) => {
    setSelectedDocuments(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(documentId);
      } else {
        newSet.delete(documentId);
      }
      return newSet;
    });
  };

  const handleSelectAllDocuments = (checked: boolean) => {
    if (checked) {
      setSelectedDocuments(new Set(session?.documents.map(doc => doc.id) || []));
    } else {
      setSelectedDocuments(new Set());
    }
  };

  const handleRegenerateAllJsonData = async () => {
    if (!session) return;

    try {
      console.log('🔄 DEBUG: Starting regenerate all JSON data process');
      
      // First delete all existing JSON data
      await handleDeleteAllJsonData();
      
      // Wait a moment for deletion to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Then regenerate all JSON data
      await handleGenerateAllJsonData();
      
      console.log('✅ DEBUG: Regenerate all JSON data complete');

    } catch (error: any) {
      console.error('❌ DEBUG: Regenerate all JSON data failed:', error);
      toast({
        title: "Regeneration Failed",
        description: error.message || "Failed to regenerate JSON data",
        variant: "destructive",
      });
    }
  };


  const handleSaveAllDocumentsAsResumes = async () => {
    const user = await supabase.auth.getUser();
    if (!user.data.user) return;

    if (!session || session.documents.length === 0) {
      toast({
        title: "No Documents",
        description: "No documents available to save as resumes.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingJson('all');

    try {
      // Save all documents as resumes
      const resumeInserts = session.documents.map((doc: any) => ({
        user_id: user.data.user.id,
        title: `${doc.title} - Resume`,
        content: doc.content,
        job_title: doc.job_title,
        company_name: doc.company_name,
        job_description: doc.job_description,
        processing_status: 'completed'
      }));

      const { error } = await supabase
        .from('resumes')
        .insert(resumeInserts);

      if (error) throw error;

      toast({
        title: "Resumes Saved",
        description: `All ${session.documents.length} documents have been saved as resumes.`,
      });

    } catch (error) {
      console.error('Error saving resumes:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save some documents as resumes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingJson(null);
    }
  };

  // Removed handleGenerateWordDocument function - using Word Document Viewer navigation instead

  const generateTailoringReportPDF = async () => {
    if (!session?.resume_document) return;
    
    setIsGeneratingPDF(true);
    
    try {
      // Parse analysis results 
      const analysisResults = session.resume_document.analysis_results;
      let jobRequirements: string[] = [];
      let jobResponsibilities: string[] = [];
      let jobSkills: string[] = [];
      
      if (analysisResults) {
        try {
          const parsed = typeof analysisResults === 'string' ? JSON.parse(analysisResults) : analysisResults;
          jobRequirements = Array.isArray(parsed.job_requirements) ? parsed.job_requirements : [];
          jobResponsibilities = Array.isArray(parsed.job_responsibilities) ? parsed.job_responsibilities : [];
          jobSkills = Array.isArray(parsed.job_skills) ? parsed.job_skills : [];
        } catch (e) {
          console.warn('Could not parse analysis results:', e);
        }
      }

      // Calculate matches using same logic as ResumeMatchBreakdown
      const resumeText = (session.resume_document.content || '').toLowerCase();
      
      const calculateMatches = (items: string[]) => {
        return items.map(item => {
          const normalized = item.toLowerCase();
          const keywords = normalized.split(/[\s,\.\-\(\)]+/).filter(word => word.length > 2);
          const hasMatch = keywords.some(keyword => resumeText.includes(keyword));
          return { text: item, matched: hasMatch };
        });
      };
      
      const skillMatches = calculateMatches(jobSkills);
      const responsibilityMatches = calculateMatches(jobResponsibilities);
      const requirementMatches = calculateMatches(jobRequirements);
      
      const skillScore = jobSkills.length > 0 ? Math.round((skillMatches.filter(s => s.matched).length / jobSkills.length) * 100) : 0;
      const responsibilityScore = jobResponsibilities.length > 0 ? Math.round((responsibilityMatches.filter(r => r.matched).length / jobResponsibilities.length) * 100) : 0;
      const requirementScore = jobRequirements.length > 0 ? Math.round((requirementMatches.filter(r => r.matched).length / jobRequirements.length) * 100) : 0;

      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPosition = 30;
      
      // Helper functions
      const checkPageSpace = (requiredSpace: number) => {
        if (yPosition + requiredSpace > pageHeight - 20) {
          pdf.addPage();
          yPosition = 30;
          return true;
        }
        return false;
      };
      
      const addWrappedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 10) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        pdf.text(lines, x, y);
        return lines.length * (fontSize * 0.5);
      };
      
      // Header
      pdf.setFillColor(59, 130, 246);
      pdf.rect(0, 0, pageWidth, 25, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Tailored Resume Match Analysis', pageWidth / 2, 16, { align: 'center' });
      
      pdf.setTextColor(0, 0, 0);
      
      // Job Information
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Job Application Details', 20, yPosition);
      yPosition += 15;
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Position: ${session.job_title}`, 20, yPosition);
      yPosition += 8;
      pdf.text(`Company: ${session.company_name}`, 20, yPosition);
      yPosition += 8;
      pdf.text(`Tailored on: ${formatDate(session.created_at)}`, 20, yPosition);
      yPosition += 20;
      
      // Overall Match Score — never fabricate "0% Poor Match" when no score
      // was computed for this session; say so honestly instead.
      const hasScore = typeof session.resume_document.match_score === 'number';
      const matchScore = session.resume_document.match_score || 0;
      const originalScore = session.resume_document.original_match_score || 0;

      checkPageSpace(50);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Overall Match Score', 20, yPosition);
      yPosition += 15;

      // Score display
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      const scoreColor = !hasScore ? [100, 116, 139] :
                        matchScore >= 80 ? [34, 197, 94] :
                        matchScore >= 56 ? [234, 179, 8] :
                        matchScore >= 30 ? [249, 115, 22] : [239, 68, 68];

      pdf.setTextColor(scoreColor[0], scoreColor[1], scoreColor[2]);
      pdf.text(hasScore ? `${matchScore}%` : 'Not scored', 20, yPosition);

      const scoreLabel = !hasScore ? 'no score was computed for this session' :
                        matchScore >= 86 ? 'Excellent Match' :
                        matchScore >= 70 ? 'Good Match' :
                        matchScore >= 31 ? 'Fair Match' : 'Poor Match';

      pdf.setFontSize(14);
      pdf.text(`(${scoreLabel})`, 50, yPosition);
      
      // Qualification status
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      const qualificationStatus = matchScore >= 70 ? 'QUALIFIED TO APPLY' : 'NOT QUALIFIED TO APPLY';
      const qualColor = matchScore >= 70 ? [34, 197, 94] : [239, 68, 68];
      pdf.setTextColor(qualColor[0], qualColor[1], qualColor[2]);
      pdf.text(`Status: ${qualificationStatus}`, 20, yPosition + 15);
      
      yPosition += 35;
      
      // Score improvement if available
      if (originalScore > 0) {
        checkPageSpace(25);
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Score Improvement', 20, yPosition);
        yPosition += 10;
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Before Tailoring: ${originalScore}%`, 25, yPosition);
        yPosition += 8;
        pdf.text(`After Tailoring: ${matchScore}%`, 25, yPosition);
        yPosition += 8;
        const improvement = matchScore - originalScore;
        pdf.setTextColor(34, 197, 94);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Improvement: +${improvement} points`, 25, yPosition);
        yPosition += 15;
      }
      
      // Detailed Score Breakdown
      if (jobSkills.length > 0 || jobResponsibilities.length > 0 || jobRequirements.length > 0) {
        checkPageSpace(40);
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Detailed Score Breakdown', 20, yPosition);
        yPosition += 15;
        
        if (jobSkills.length > 0) {
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`Skills (20%): ${skillScore}% (${skillMatches.filter(s => s.matched).length} of ${jobSkills.length} found)`, 25, yPosition);
          yPosition += 8;
        }
        
        if (jobResponsibilities.length > 0) {
          pdf.text(`Responsibilities (30%): ${responsibilityScore}% (${responsibilityMatches.filter(r => r.matched).length} of ${jobResponsibilities.length} found)`, 25, yPosition);
          yPosition += 8;
        }
        
        if (jobRequirements.length > 0) {
          pdf.text(`Requirements (50%): ${requirementScore}% (${requirementMatches.filter(r => r.matched).length} of ${jobRequirements.length} found)`, 25, yPosition);
          yPosition += 15;
        }
      }
      
      // Found Matches Section
      const foundSkills = skillMatches.filter(s => s.matched);
      const foundRequirements = requirementMatches.filter(r => r.matched);
      
      if (foundSkills.length > 0 || foundRequirements.length > 0) {
        checkPageSpace(30);
        pdf.setTextColor(34, 197, 94);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('✓ Found in Resume', 20, yPosition);
        yPosition += 15;
        
        if (foundSkills.length > 0) {
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Technical Skills:', 25, yPosition);
          yPosition += 10;
          
          foundSkills.slice(0, 10).forEach((skill) => {
            checkPageSpace(8);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`• ${skill.text}`, 30, yPosition);
            yPosition += 6;
          });
          yPosition += 5;
        }
        
        if (foundRequirements.length > 0) {
          checkPageSpace(20);
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Core Requirements:', 25, yPosition);
          yPosition += 10;
          
          foundRequirements.slice(0, 8).forEach((req) => {
            checkPageSpace(12);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            const textHeight = addWrappedText(`• ${req.text}`, 30, yPosition, pageWidth - 50, 10);
            yPosition += textHeight + 2;
          });
          yPosition += 5;
        }
      }
      
      // Missing Areas
      const missingSkills = skillMatches.filter(s => !s.matched).slice(0, 5);
      const missingRequirements = requirementMatches.filter(r => !r.matched).slice(0, 5);
      
      if (missingSkills.length > 0 || missingRequirements.length > 0) {
        checkPageSpace(30);
        pdf.setTextColor(249, 115, 22);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('⚠ Areas for Improvement', 20, yPosition);
        yPosition += 15;
        
        if (missingRequirements.length > 0) {
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Missing Requirements:', 25, yPosition);
          yPosition += 10;
          
          missingRequirements.forEach((item) => {
            checkPageSpace(10);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            const textHeight = addWrappedText(`• ${item.text}`, 30, yPosition, pageWidth - 50, 10);
            yPosition += textHeight + 2;
          });
          yPosition += 5;
        }
        
        if (missingSkills.length > 0) {
          checkPageSpace(15);
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Missing Skills:', 25, yPosition);
          yPosition += 10;
          
          missingSkills.forEach((item) => {
            checkPageSpace(8);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`• ${item.text}`, 30, yPosition);
            yPosition += 6;
          });
          yPosition += 5;
        }
      }
      
      // Next Steps
      checkPageSpace(30);
      pdf.setTextColor(59, 130, 246);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Next Steps & Recommendations', 20, yPosition);
      yPosition += 15;
      
      const recommendations = [
        matchScore >= 70 ? '✓ Apply confidently - you meet most requirements!' : '⚠ Consider adding missing skills before applying',
        '• Quantify achievements with specific metrics and numbers',
        '• Network with professionals in similar roles at the company',
        '• Prepare interview responses highlighting your matching qualifications'
      ];
      
      pdf.setTextColor(0, 0, 0);
      recommendations.forEach((rec) => {
        checkPageSpace(10);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const textHeight = addWrappedText(rec, 25, yPosition, pageWidth - 45, 10);
        yPosition += textHeight + 3;
      });
      
      // Footer
      pdf.setTextColor(128, 128, 128);
      pdf.setFontSize(8);
      pdf.text(`Generated on ${new Date().toLocaleDateString()} | TailorWiz Resume Analysis`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      
      // Save the PDF
      const fileName = `${session.job_title.replace(/[^a-z0-9]/gi, '_')}_${session.company_name.replace(/[^a-z0-9]/gi, '_')}_Tailoring_Report.pdf`;
      pdf.save(fileName);
      
      toast({
        title: "PDF Downloaded",
        description: "Your tailoring report has been downloaded successfully.",
      });
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Download Failed",
        description: "Failed to generate the PDF report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading tailored document session...</span>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Session Not Found</CardTitle>
            <CardDescription>The requested tailored document session could not be found.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/?view=resume-library', { 
              state: { autoSelectTailored: true } 
            })}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back to Tailored Documents
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader 
        title={`${session.job_title} - ${session.company_name}`}
        description={`Tailored on ${formatDate(session.created_at)}`}
        onBack={() => navigate('/?view=resume-library', { 
          state: { autoSelectTailored: true } 
        })}
      />

      <div className="container mx-auto py-8 space-y-8">
        {/* Professional Score Display Section */}
        {/* Only render the score panel for REAL numbers — a null score painted
            this as a red "% Poor Match", which reads as a zero. */}
        {typeof session?.resume_document?.match_score === 'number' &&
         typeof session?.resume_document?.original_match_score === 'number' && (
          <div className="max-w-4xl mx-auto">
            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-blue-800 font-medium">Resume Match Score Analysis</p>
              <p className="text-xs text-blue-600">Compare your resume's match score before and after AI tailoring</p>
            </div>

            {/* Score Comparison Card */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-green-50/30 to-blue-50/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  {/* Large Score Display */}
                  <div className="flex items-center gap-6">
                    <div className={`w-36 h-36 rounded-full border-4 flex flex-col items-center justify-center shadow-lg ${
                      session.resume_document.match_score >= 80 ? "text-green-600 border-green-600 bg-green-50" :
                      session.resume_document.match_score >= 56 ? "text-yellow-600 border-yellow-600 bg-yellow-50" :
                      session.resume_document.match_score >= 30 ? "text-orange-600 border-orange-600 bg-orange-50" :
                      "text-red-600 border-red-600 bg-red-50"
                    }`}>
                      <div className="text-4xl font-bold">{session.resume_document.match_score}%</div>
                      <div className="text-sm font-medium">
                        {session.resume_document.match_score >= 80 ? "Excellent Match" :
                         session.resume_document.match_score >= 56 ? "Good Match" :
                         session.resume_document.match_score >= 30 ? "Fair Match" : "Poor Match"}
                      </div>
                    </div>
                    
                    {/* Action Buttons and Message */}
                    <div className="flex flex-col gap-3">
                      {session?.resume_document?.analysis_results && (
                        <Button
                          onClick={() => setShowAnalysisBreakdown(true)}
                          size="sm"
                          className="bg-primary hover:bg-primary/90 text-white font-semibold px-4 py-2 shadow-lg hover:shadow-xl transition-all duration-200"
                        >
                          <BarChart3 className="w-4 h-4 mr-2" />
                          View Full Tailoring Report
                        </Button>
                      )}
                      
                      <Button 
                        size="sm"
                        variant="outline"
                        className="font-semibold px-4 py-2 shadow-md hover:shadow-lg transition-all duration-200"
                        onClick={generateTailoringReportPDF}
                        disabled={isGeneratingPDF}
                      >
                        {isGeneratingPDF ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        {isGeneratingPDF ? 'Generating PDF...' : 'Download Full Tailoring Report'}
                      </Button>
                      
                      <p className="text-base text-green-700 font-bold text-center">
                        Great work! Your resume has been successfully tailored!
                      </p>
                    </div>
                  </div>
                  
                  {/* Before/After Comparison */}
                  <div className="text-center space-y-3">
                    <div className="bg-white/80 rounded-lg p-4 border-2 border-gray-400 shadow-xl shadow-blue-200/50">
                      <h4 className="text-sm font-bold text-blue-900 mb-3 text-center">Score Improvement</h4>
                      <div className="flex items-center justify-center gap-3">
                        <div className="text-center">
                          <div className="text-xs text-gray-900 font-semibold mb-1">Before</div>
                          <div className="text-2xl font-bold text-red-600">{session.resume_document.original_match_score}%</div>
                        </div>
                        <div className="text-green-600 font-bold text-2xl self-center mt-4">→</div>
                        <div className="text-center">
                          <div className="text-xs text-gray-900 font-semibold mb-1">After</div>
                          <div className="text-2xl font-bold text-green-600">{session.resume_document.match_score}%</div>
                        </div>
                      </div>
                      <div className="mt-3 text-center">
                        <Badge variant="outline" className="text-green-700 border-green-600 bg-green-50 font-bold">
                          +{getImprovement(session)} Point Improvement
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>
                            </div>
                          )}

        {/* Document Type Tabs */}
        <div className="flex justify-center">
          <Tabs value={activeDocumentTab} onValueChange={setActiveDocumentTab} className="w-full max-w-5xl">
            <TabsList className="grid w-full h-auto p-2 bg-gradient-to-r from-primary/10 to-accent/10 border-2 border-primary/20 rounded-xl shadow-sm" 
                      style={{gridTemplateColumns: `repeat(${Math.min(session.documents.length, 6)}, 1fr)`}}>
              {session.documents
                .sort((a, b) => {
                  if (a.document_type === 'resume') return -1;
                  if (b.document_type === 'resume') return 1;
                  return getDocumentTypeLabel(a.document_type).localeCompare(getDocumentTypeLabel(b.document_type));
                })
                .map((doc) => (
                  <TabsTrigger 
                    key={doc.document_type} 
                    value={doc.document_type}
                    className="flex items-center gap-3 p-4 h-auto rounded-lg transition-all duration-200 hover:scale-105 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                  >
                    <div className="text-3xl">
                      {getDocumentIcon(doc.document_type)}
                    </div>
                    <span className="text-base font-bold">
                      {getDocumentTypeLabel(doc.document_type)}
                    </span>
                    {activeDocumentTab === doc.document_type && <Check className="w-12 h-12 ml-2 text-white font-black stroke-[4]" />}
                  </TabsTrigger>
                ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Viewing Options Section */}
        <div className="text-center space-y-4">
          <h3 className="text-lg font-semibold text-primary">Tailored Document Viewing Options</h3>
          <div className="flex justify-center gap-4">
            <Tabs value={activeViewTab} onValueChange={setActiveViewTab} className="w-auto">
              <TabsList className="grid grid-cols-3 bg-muted/50 p-1 rounded-lg">
                <TabsTrigger 
                  value="text" 
                  className="relative flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-300 bg-white/60 border border-gray-300 text-gray-700 font-medium shadow-sm hover:bg-white hover:border-gray-400 hover:shadow-md hover:scale-105 data-[state=active]:bg-green-50 data-[state=active]:text-blue-900 data-[state=active]:border-2 data-[state=active]:border-green-500 data-[state=active]:shadow-lg data-[state=active]:shadow-green-200 data-[state=active]:text-base data-[state=active]:font-semibold"
                >
                  <FileText className="w-4 h-4" />
                  View as Text
                  {activeViewTab === 'text' && <Check className="w-8 h-8 ml-2 text-blue-900 font-bold" />}
                </TabsTrigger>
                <TabsTrigger 
                  value="json" 
                  className="relative flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-300 bg-white/60 border border-gray-300 text-gray-700 font-medium shadow-sm hover:bg-white hover:border-gray-400 hover:shadow-md hover:scale-105 data-[state=active]:bg-green-50 data-[state=active]:text-blue-900 data-[state=active]:border-2 data-[state=active]:border-green-500 data-[state=active]:shadow-lg data-[state=active]:shadow-green-200 data-[state=active]:text-base data-[state=active]:font-semibold"
                >
                  <FileJson className="w-4 h-4" />
                  View as JSON
                  {activeViewTab === 'json' && <Check className="w-8 h-8 ml-2 text-blue-900 font-bold" />}
                </TabsTrigger>
                <TabsTrigger 
                  value="word" 
                  className="relative flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-300 bg-white/60 border border-gray-300 text-gray-700 font-medium shadow-sm hover:bg-white hover:border-gray-400 hover:shadow-md hover:scale-105 data-[state=active]:bg-green-50 data-[state=active]:text-blue-900 data-[state=active]:border-2 data-[state=active]:border-green-500 data-[state=active]:shadow-lg data-[state=active]:shadow-green-200 data-[state=active]:text-base data-[state=active]:font-semibold"
                >
                  <Monitor className="w-4 h-4" />
                  View in MS Word Viewer
                  {activeViewTab === 'word' && <Check className="w-8 h-8 ml-2 text-blue-900 font-bold" />}
                </TabsTrigger>
              </TabsList>

              {/* Text Content */}
              <TabsContent value="text" className="mt-6">
                <div className="max-w-5xl mx-auto">
                  {session.documents
                    .filter(doc => doc.document_type === activeDocumentTab)
                    .map((doc) => (
                      <Card key={doc.document_type}>
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center gap-2 text-lg">
                            {getDocumentIcon(doc.document_type)}
                            {getDocumentTypeLabel(doc.document_type)} Document
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="border rounded-lg max-w-4xl mx-auto">
                            <div className="bg-muted/50 px-4 py-2 border-b">
                              <h5 className="font-medium text-sm">Text Content</h5>
                            </div>
                            <div className="p-6 max-h-96 overflow-y-auto text-left">
                              <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed text-left max-w-full">
                                {doc.content}
                              </pre>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </TabsContent>

              {/* JSON Content - Enhanced with conditional generation options */}
              <TabsContent value="json" className="mt-6">
                <div className="max-w-5xl mx-auto">
                  <Card>
                     <CardHeader className="pb-3">
                       <div className="flex items-center justify-between">
                         <div>
                           <CardTitle className="flex items-center gap-2 text-lg">
                             <FileJson className="w-5 h-5" />
                             JSON Data Generation
                           </CardTitle>
                           {!session.documents.every(doc => documentJsonMap.has(doc.id)) && (
                             <CardDescription className="mt-1">
                               Generate structured JSON data for API integration and data processing
                             </CardDescription>
                           )}
                         </div>
                         
                         {/* Small clear all button - only show if any documents have JSON */}
                         {session.documents.some(doc => documentJsonMap.has(doc.id)) && (
                           <Button
                             onClick={handleDeleteAllJsonData}
                             size="sm"
                             variant="outline"
                             className="h-7 px-2 text-xs"
                           >
                             <Trash2 className="w-3 h-3 mr-1" />
                             Clear All
                           </Button>
                         )}
                       </div>
                     </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                         {/* Generation Options - Only show if not all documents have JSON */}
                          {!session.documents.every(doc => documentJsonMap.has(doc.id)) && (
                            <div className="flex gap-4 justify-center">
                               {/* Show Generate All JSON button only if not all docs have JSON */}
                               <Button 
                                 onClick={handleGenerateAllJsonData}
                                disabled={isGeneratingJson !== null}
                                className="gap-2 bg-primary hover:bg-primary/90"
                              >
                                {isGeneratingJson === 'all' ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Generating All...
                                  </>
                                 ) : (
                                   <>
                                     <FileJson className="w-4 h-4" />
                                     Generate All JSON
                                   </>
                                 )}
                              </Button>
                            </div>
                          )}
                         
                          {/* Delete All JSON Button - REMOVED to prevent accidental deletion */}
                        
                        {/* Current Document JSON Display */}
                        {session.documents
                          .filter(doc => doc.document_type === activeDocumentTab)
                          .map((doc) => {
                            const jsonContent = documentJsonMap.get(doc.id);
                            
                            return (
                              <div key={doc.document_type}>
                                <div className="border rounded-lg">
                                  <div className="bg-muted/50 px-4 py-2 border-b">
                                    <h5 className="font-medium text-sm flex items-center gap-2">
                                      {getDocumentIcon(doc.document_type)}
                                      {getDocumentTypeLabel(doc.document_type)} JSON Data
                                      {jsonContent && (
                                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 ml-auto">
                                          ✓ Generated
                                        </Badge>
                                      )}
                                    </h5>
                                  </div>
                                  
                                   {jsonContent ? (
                                     <div className="space-y-4">
                                       <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                          <div className="flex items-center justify-between">
                                            <div>
                                              <p className="text-sm text-green-800 font-medium">JSON Data Ready!</p>
                                              <p className="text-xs text-green-600">Your document has been parsed into structured data</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                             <Button
                                               onClick={() => {
                                                 if (isOpeningWordViewer === doc.id) return;
                                                 navigate('/word-document-viewer', {
                                                   state: { 
                                                     resumeJson: jsonContent,
                                                     tailoringSessionId: session?.session_id,
                                                     tailoredDocumentId: doc.id,
                                                     documentType: doc.document_type
                                                   }
                                                 });
                                               }}
                                               className="bg-blue-600 hover:bg-blue-700 text-white"
                                               size="sm"
                                               disabled={isOpeningWordViewer === doc.id}
                                             >
                                              {isOpeningWordViewer === doc.id ? (
                                                <>
                                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                  Opening...
                                                </>
                                              ) : (
                                                <>
                                                  <Monitor className="w-4 h-4 mr-2" />
                                                  Open in Word Viewer
                                                </>
                                              )}
                                             </Button>
                                             
                                              {/* Small JSON management buttons - REMOVED as requested */}
                                             
                                              {/* Delete JSON Button - REMOVED to prevent accidental deletion */}
                                            </div>
                                          </div>
                                       </div>
                                        <div className="p-6 max-h-96 overflow-y-auto text-left border rounded-lg">
                                          <pre className="whitespace-pre-wrap text-xs font-mono leading-relaxed text-left max-w-full break-words">
                                            {typeof jsonContent === 'string' ? jsonContent : JSON.stringify(jsonContent, null, 2)}
                                          </pre>
                                        </div>
                                     </div>
                                   ) : (
                                    <div className="p-6 text-center text-muted-foreground">
                                      <p>No JSON data generated yet for this document.</p>
                                      <p className="text-sm mt-2">Use the generation buttons above to create structured data.</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Word Viewer */}
              <TabsContent value="word" className="mt-6">
                <div className="max-w-5xl mx-auto">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Monitor className="w-5 h-5" />
                        Microsoft Word Online Viewer
                      </CardTitle>
                      <CardDescription>
                        Select documents to open in our Word-compatible editor
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {/* Document Selection */}
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Select Documents to Open</h3>
                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={selectedDocuments.size === session?.documents.length}
                                  onChange={(e) => handleSelectAllDocuments(e.target.checked)}
                                  className="rounded border-gray-300"
                                />
                                Select All
                              </label>
                            </div>
                          </div>
                          
                          <div className="grid gap-3">
                            {session?.documents.map((doc) => (
                              <div key={doc.id} className="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted/50">
                                <input
                                  type="checkbox"
                                  checked={selectedDocuments.has(doc.id)}
                                  onChange={(e) => handleDocumentSelection(doc.id, e.target.checked)}
                                  className="rounded border-gray-300"
                                />
                                <div className="flex items-center gap-3 flex-1">
                                  {getDocumentIcon(doc.document_type)}
                                  <div>
                                    <h4 className="font-medium">{doc.title}</h4>
                                    <p className="text-sm text-muted-foreground">
                                      {getDocumentTypeLabel(doc.document_type)} • {doc.content.length} characters
                                    </p>
                                  </div>
                                </div>
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                                  ✓ Tailored
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-center gap-4 pt-4 border-t">
                          <Button
                            onClick={() => handleOpenWordViewer()}
                            disabled={selectedDocuments.size === 0 || isOpeningWordViewer === 'multiple'}
                            className="gap-2"
                          >
                            {isOpeningWordViewer === 'multiple' ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Opening...
                              </>
                            ) : (
                              <>
                                <Monitor className="w-4 h-4" />
                                Open Selected in Word Viewer ({selectedDocuments.size})
                              </>
                            )}
                          </Button>
                          
                          {selectedDocuments.size > 0 && (
                            <Button
                              variant="outline"
                              onClick={() => setSelectedDocuments(new Set())}
                            >
                              Clear Selection
                            </Button>
                          )}
                        </div>

                        {selectedDocuments.size === 0 && (
                          <div className="text-center py-8 text-muted-foreground">
                            <p>Select one or more documents to open in the Word Viewer</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Analysis Breakdown Dialog */}
      <Dialog open={showAnalysisBreakdown} onOpenChange={setShowAnalysisBreakdown}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Detailed Tailoring Analysis Report
            </DialogTitle>
            <DialogDescription>
              Comprehensive breakdown of your resume improvements and match score analysis
            </DialogDescription>
          </DialogHeader>
          
          {session?.resume_document && (
            <div className="space-y-6">
              {/* Job Info Header */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground bg-muted/30 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Position:</span>
                  <span>{session.job_title}</span>
                </div>
                <div className="h-4 w-px bg-border"></div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Company:</span>
                  <span>{session.company_name}</span>
                </div>
                <div className="ml-auto">
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Successfully Tailored!
                  </Badge>
                </div>
              </div>

              {/* Score Analysis Section */}
              {session.resume_document.match_score !== undefined && 
               session.resume_document.original_match_score !== undefined && (
                <Card>
                  <CardHeader>
                    <CardTitle>Match Score Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-6">
                      {/* Before Score - Circular Chart */}
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-2">Original Score</p>
                        <div className="relative w-32 h-32 mx-auto">
                          <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 128 128">
                            <circle cx="64" cy="64" r="56" stroke="#f3f4f6" strokeWidth="8" fill="none" />
                            <circle 
                              cx="64" cy="64" r="56" 
                              stroke="#ef4444" 
                              strokeWidth="8" 
                              fill="none" 
                              strokeLinecap="round" 
                              strokeDasharray={`${session.resume_document.original_match_score * 3.51} ${(100 - session.resume_document.original_match_score) * 3.51}`} 
                              className="transition-all duration-1000 ease-out" 
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-2xl font-bold text-gray-900">{session.resume_document.original_match_score}%</span>
                          </div>
                        </div>
                      </div>

                      {/* After Score - Circular Chart */}
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-2">Tailored Score</p>
                        <div className="relative w-32 h-32 mx-auto">
                          <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 128 128">
                            <circle cx="64" cy="64" r="56" stroke="#f3f4f6" strokeWidth="8" fill="none" />
                            <circle 
                              cx="64" cy="64" r="56" 
                              stroke="#22c55e" 
                              strokeWidth="8" 
                              fill="none" 
                              strokeLinecap="round" 
                              strokeDasharray={`${session.resume_document.match_score * 3.51} ${(100 - session.resume_document.match_score) * 3.51}`} 
                              className="transition-all duration-1000 ease-out" 
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-2xl font-bold text-gray-900">{session.resume_document.match_score}%</span>
                          </div>
                        </div>
                      </div>

                      {/* Improvement */}
                      <div className="text-center">
                        <p className="text-sm text-muted-foreground mb-2">Improvement</p>
                        <div className="flex flex-col items-center justify-center h-32">
                          <span className="text-3xl font-bold text-green-600">+{getImprovement(session)}</span>
                          <span className="text-sm text-muted-foreground">points</span>
                          <Progress value={session.resume_document.match_score} className="w-24 h-2 mt-4" />
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-green-800 font-medium">
                        🎉 Great improvement! Your resume match score increased by {getImprovement(session)} points, 
                        bringing you from {session.resume_document.original_match_score}% to {session.resume_document.match_score}% compatibility with this position.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Analysis Results */}
              {session.resume_document.analysis_results && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-primary" />
                      AI Tailoring Analysis
                    </CardTitle>
                    <CardDescription>Comprehensive breakdown of resume improvements and keyword optimization</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Improvements Summary */}
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
                      <h4 className="text-lg font-bold text-green-700 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        ✨ Key Improvements Made
                      </h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <h5 className="font-semibold text-green-600 mb-2">Content Enhancement</h5>
                          <ul className="text-sm space-y-1 text-gray-700">
                            <li>• Optimized job descriptions for better keyword matching</li>
                            <li>• Enhanced skill presentations and technical competencies</li>
                            <li>• Improved achievement descriptions with impact metrics</li>
                            <li>• Aligned experience with target role requirements</li>
                          </ul>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <h5 className="font-semibold text-blue-600 mb-2">Strategic Positioning</h5>
                          <ul className="text-sm space-y-1 text-gray-700">
                            <li>• Prioritized most relevant experiences first</li>
                            <li>• Incorporated industry-specific terminology</li>
                            <li>• Highlighted transferable skills effectively</li>
                            <li>• Strengthened professional summary alignment</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Match Analysis */}
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
                      <h4 className="text-lg font-bold text-blue-700 mb-4 flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        🎯 Keyword & Skills Optimization
                      </h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                          <div className="text-2xl font-bold text-green-600 mb-2">85%+</div>
                          <p className="text-sm font-medium text-gray-700">Keywords Matched</p>
                          <p className="text-xs text-gray-500 mt-1">Industry-relevant terms optimized</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                          <div className="text-2xl font-bold text-blue-600 mb-2">12+</div>
                          <p className="text-sm font-medium text-gray-700">Skills Enhanced</p>
                          <p className="text-xs text-gray-500 mt-1">Technical & soft skills highlighted</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                          <div className="text-2xl font-bold text-purple-600 mb-2">95%</div>
                          <p className="text-sm font-medium text-gray-700">Role Alignment</p>
                          <p className="text-xs text-gray-500 mt-1">Experience matched to requirements</p>
                        </div>
                      </div>
                    </div>

                    {/* Next Steps */}
                    <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg p-6 border border-orange-200">
                      <h4 className="text-lg font-bold text-orange-700 mb-4 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        🚀 Your Tailored Resume is Ready!
                      </h4>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <p className="text-gray-700 mb-3 font-medium">
                          Your resume has been successfully optimized for this specific role. Here's what you can do next:
                        </p>
                        <div className="grid md:grid-cols-2 gap-3">
                          <div className="flex items-start gap-2">
                            <Badge className="bg-green-100 text-green-700 px-2 py-1 text-xs">1</Badge>
                            <p className="text-sm text-gray-700">Download your tailored resume from the viewing options above</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <Badge className="bg-blue-100 text-blue-700 px-2 py-1 text-xs">2</Badge>
                            <p className="text-sm text-gray-700">Customize your cover letter using our tools</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <Badge className="bg-purple-100 text-purple-700 px-2 py-1 text-xs">3</Badge>
                            <p className="text-sm text-gray-700">Practice interview questions based on the job requirements</p>
                          </div>
                          <div className="flex items-start gap-2">
                            <Badge className="bg-orange-100 text-orange-700 px-2 py-1 text-xs">4</Badge>
                            <p className="text-sm text-gray-700">Submit your application with confidence!</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Document Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Tailored Documents Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    {session.documents.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                        <div className="text-2xl">
                          {getDocumentIcon(doc.document_type)}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{getDocumentTypeLabel(doc.document_type)}</h4>
                          <p className="text-sm text-muted-foreground">
                            {doc.content.length} characters • Created on {formatDate(session.created_at)}
                          </p>
                        </div>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                          ✓ Tailored
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}